import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import type { UseFormProps } from 'react-hook-form';
import { z } from 'zod';

import { SUPPORTED_FILE_TYPES } from '@features/investigations/api/files.types';

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);

export const TARGET_TYPES = ['user', 'ip', 'host'] as const;

const HOST_TARGET_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;
const USERNAME_TARGET_REGEX = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_.@/\\+-]+$/;
const FILE_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_ ]*$/;

const TraceNameTarget = z
  .string()
  .trim()
  .min(1, 'Name is required')
  .max(255, 'Name cannot exceed 255 characters')
  .regex(FILE_NAME_REGEX, {
    message: 'Name must start with a letter or digit and can contain spaces or underscores',
  });

const UserTarget = z.object({
  type: z.literal('user'),
  value: z
    .string()
    .trim()
    .min(1, 'Username is required')
    .regex(USERNAME_TARGET_REGEX, {
      message: String.raw`Invalid Username format. Allowed characters are letters, numbers, and special characters: . _ @ / \+ -`,
    }),
});

const HostTarget = z.object({
  type: z.literal('host'),
  value: z.string().trim().min(1, 'Host is required').regex(HOST_TARGET_REGEX, {
    message:
      'Invalid hostname format. Hostname must start with a letter or number and can include letters, numbers, hyphens, and underscores.',
  }),
});

const IpTarget = z.object({
  type: z.literal('ip'),
  value: z.ipv4({ message: 'Invalid IP Address format (e.g., 192.168.1.1)' }),
});

const DateRangeSchema = z
  .object({
    from: z.date(),
    to: z.date(),
  })
  .optional()
  .refine((r) => !!r?.from && !!r?.to, {
    message: 'Please select both start and end dates',
  })
  .refine(
    (r) => {
      if (!r?.to) return true;
      const endTime = dayjs.utc(r.to).startOf('minute');
      const nowUtc = dayjs.utc().startOf('minute');

      // we account for a few minutes of latency,
      return endTime.isSameOrBefore(nowUtc.add(2, 'minute'));
    },
    {
      message: 'File end time cannot be in the future (UTC)',
    },
  );

const TargetSchema = z.discriminatedUnion('type', [UserTarget, HostTarget, IpTarget]);
const InvestigationTargetsSchema = z.array(TargetSchema).min(1, 'At least one scope condition is required');
const TargetInputSchema = z.object({
  type: z.enum(TARGET_TYPES),
  value: z.string(),
});

export const InvestigationFormSchema = z
  .object({
    type: z.enum(SUPPORTED_FILE_TYPES),
    traceName: TraceNameTarget,
    dateRange: DateRangeSchema,
    assets: z.array(z.string()).min(1, 'At least one application instance is required'),
    targets: z.array(TargetInputSchema),
    inquiryUsername: z.string().optional(),
    sendEmailNotification: z.boolean().optional(),
    reservationId: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'INQUIRY' && !data.inquiryUsername?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Username is required',
        path: ['inquiryUsername'],
      });
      return;
    }

    if (data.type === 'INVESTIGATION') {
      if (data.dateRange?.from && data.dateRange?.to) {
        const maxMs = 14 * 24 * 60 * 60 * 1000;
        const diff = dayjs.utc(data.dateRange.to).diff(dayjs.utc(data.dateRange.from));

        if (diff > maxMs) {
          ctx.addIssue({
            code: 'custom',
            message:
              'Investigations cannot be longer than 14 days. If you need to cover a larger interval, please create multiple investigations.',
            path: ['dateRange'],
          });
        }
      }

      const parsed = InvestigationTargetsSchema.safeParse(data.targets);

      if (parsed.success) return;

      parsed.error.issues.forEach((issue) => {
        ctx.addIssue({
          code: 'custom',
          message: issue.message,
          path: ['targets', ...issue.path],
        });
      });
    }
  });

// TODO(file-launch-renaming): rename schema/value/options exports to neutral file-launch naming.
export type NewInvestigationFormValues = z.infer<typeof InvestigationFormSchema>;

export const getNewInvestigationFormOptions = (): UseFormProps<NewInvestigationFormValues> => ({
  resolver: zodResolver(InvestigationFormSchema),
  defaultValues: {
    type: 'INVESTIGATION',
    traceName: '',
    dateRange: undefined,
    assets: [],
    targets: [{ type: 'user', value: '' }],
    inquiryUsername: '',
    sendEmailNotification: true,
    reservationId: '',
  },
  mode: 'onSubmit',
  criteriaMode: 'all',
});
